import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext, useWatch, FieldError } from 'react-hook-form';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconTrash, IconUserPlus } from '@/icons';
import IconButton from '../core/IconButton';
import Button from '../core/Button';
import { FormSection } from '../FormSection';
import { cn } from '@/helpers/classnames';
import { FormElement } from '../FormElement';
import { InputBusStop } from '../Inputs/InputBusStop';
import { InputText } from '../Inputs/InputText';
import { InputPostalCode } from '../Inputs/InputPostalCode';
import { SchemaType } from '@/hooks/useStudentFormSchema';
import { WarningBanner } from '../core/WarningBanner';
import { HelpTooltip } from '../core/HelpTooltip';
import { EMAIL_DESCRIPTION } from '@/components/Import/fieldsLegendData';

type StudentFormType =
  | SchemaType<'create' | 'update', 'transportation'>
  | SchemaType<'create' | 'update', 'travels'>;

interface StudentAddressesProps {
  editMode: boolean;
  onClickButton?: () => void;
  canEdit?: boolean;
}

export const StudentAddresses = ({
  editMode,
  onClickButton,
  canEdit,
}: StudentAddressesProps) => {
  const { formatMessage } = useIntl();
  const { watch, getValues, setValue } = useFormContext<StudentFormType>();
  const addGuardianButtonRef = useRef<HTMLButtonElement>(null);
  const shouldFocusAddGuardianRef = useRef(false);
  const shouldFocusGuardian2Ref = useRef(false);

  const guardian2Address = watch('guardian2_address');
  const guardian2BusStop = watch('guardian2_bus_stop');
  const guardian2Email = watch('guardian2_email');

  const hasGuardian2 =
    !!guardian2Address?.street ||
    !!guardian2Address?.city ||
    !!guardian2BusStop ||
    !!guardian2Email;

  const [showGuardian2, setShowGuardian2] = useState(hasGuardian2);
  const twoGuardians = showGuardian2 || hasGuardian2;

  const addGuardian = useCallback(() => {
    shouldFocusGuardian2Ref.current = true;
    setShowGuardian2(true);
  }, []);

  const removeGuardian = useCallback(
    (guardianIndex: 1 | 2) => {
      if (guardianIndex === 1) {
        const promotedAddress = getValues('guardian2_address');
        const fieldOptions = { shouldDirty: true, shouldValidate: true };
        setValue(
          'guardian1_address.street' as never,
          (promotedAddress?.street ?? '') as never,
          fieldOptions,
        );
        setValue(
          'guardian1_address.zip_code' as never,
          (promotedAddress?.zip_code ?? '') as never,
          fieldOptions,
        );
        setValue(
          'guardian1_address.city' as never,
          (promotedAddress?.city ?? '') as never,
          fieldOptions,
        );
        setValue(
          'guardian1_address.care_of' as never,
          (promotedAddress?.care_of ?? '') as never,
          fieldOptions,
        );
        setValue(
          'guardian1_bus_stop',
          getValues('guardian2_bus_stop') as never,
          { shouldDirty: true, shouldValidate: true },
        );
        setValue('guardian1_email', getValues('guardian2_email') as never, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      setValue('guardian2_address', undefined as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('guardian2_bus_stop', undefined as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('guardian2_email', undefined as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setShowGuardian2(false);
      shouldFocusAddGuardianRef.current = true;
    },
    [getValues, setValue],
  );

  const removeFirstGuardian = useCallback(() => {
    removeGuardian(1);
  }, [removeGuardian]);

  const removeSecondGuardian = useCallback(() => {
    removeGuardian(2);
  }, [removeGuardian]);

  useEffect(() => {
    if (!shouldFocusAddGuardianRef.current) return;
    if (showGuardian2 || hasGuardian2) return;
    shouldFocusAddGuardianRef.current = false;
    addGuardianButtonRef.current?.focus();
  }, [showGuardian2, hasGuardian2]);

  useEffect(() => {
    if (!shouldFocusGuardian2Ref.current || !showGuardian2) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById('guardian2_email')?.focus();
      shouldFocusGuardian2Ref.current = false;
    });
    return () => cancelAnimationFrame(frame);
  }, [showGuardian2]);

  return (
    <FormSection
      title={<FormattedMessage defaultMessage="Vårdnadshavare" />}
      editMode={editMode}
      onClickButton={onClickButton}
      canSeeButton={onClickButton ? canEdit : undefined}
    >
      {editMode && (
        <WarningBanner announce={false}>
          <FormattedMessage
            id="page.students.manualEditWarning"
            defaultMessage="Manuell redigering av personer i en elevkategori kommer bli överskrivet vid nästa import om inte samma ändringar finns i importfilen."
          />
        </WarningBanner>
      )}
      <GuardianFields
        title={formatMessage({ defaultMessage: 'Vårdnadshavare 1' })}
        guardianIndex={1}
        editMode={editMode}
        className={cn(editMode && 'mb-2')}
        onRemove={editMode && twoGuardians ? removeFirstGuardian : undefined}
      />
      {twoGuardians && (
        <GuardianFields
          title={formatMessage({ defaultMessage: 'Vårdnadshavare 2' })}
          guardianIndex={2}
          editMode={editMode}
          className={cn(editMode && 'mb-2')}
          onRemove={editMode ? removeSecondGuardian : undefined}
        />
      )}
      {editMode && !twoGuardians && (
        <div className="w-full rounded-md border border-dashed border-background-400 p-4">
          <Button
            ref={addGuardianButtonRef}
            startIcon={<IconUserPlus />}
            onClick={addGuardian}
          >
            <FormattedMessage defaultMessage="Lägg till vårdnadshavare" />
          </Button>
        </div>
      )}
    </FormSection>
  );
};

interface GuardianFieldsProps {
  guardianIndex: 1 | 2;
  title: string;
  editMode: boolean;
  className?: string;
  onRemove?: () => void;
}

type GuardianErrors = {
  guardian1_email?: FieldError;
  guardian1_bus_stop?: FieldError;
  guardian1_address?: {
    care_of?: FieldError;
    street?: FieldError;
    zip_code?: FieldError;
    city?: FieldError;
  };
  guardian2_email?: FieldError;
  guardian2_bus_stop?: FieldError;
  guardian2_address?: {
    care_of?: FieldError;
    street?: FieldError;
    zip_code?: FieldError;
    city?: FieldError;
  };
};

const GuardianFields = ({
  guardianIndex,
  title,
  editMode,
  className,
  onRemove,
}: GuardianFieldsProps) => {
  const { formatMessage } = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext<StudentFormType>();

  const prefix = `guardian${guardianIndex}` as const;
  const addressField = `${prefix}_address` as const;
  const busStopField = `${prefix}_bus_stop` as const;
  const emailField = `${prefix}_email` as const;

  const careOf = useWatch({
    control,
    name: `${addressField}.care_of` as never,
  }) as string | undefined;

  const typedErrors = errors as GuardianErrors;
  const addressErrors = typedErrors[addressField];
  const busStopError = typedErrors[busStopField];
  const emailError = typedErrors[emailField];

  return (
    <div
      className={cn(
        'flex flex-col rounded-md border border-dashed border-background-400',
        className,
      )}
    >
      <h3 className="flex h-fit w-full items-center rounded-md bg-background-100 p-2 text-base font-semibold">
        {title}
        {onRemove && (
          <IconButton
            aria-label={formatMessage(
              { defaultMessage: 'Ta bort vårdnadshavare {number}' },
              { number: guardianIndex },
            )}
            icon={<IconTrash aria-hidden="true" className="size-5" />}
            className="ml-auto"
            onClick={onRemove}
          />
        )}
      </h3>
      <div className="flex flex-col gap-2 p-4">
        <FormElement
          id={emailField}
          label={<FormattedMessage defaultMessage="E-post" />}
          labelHint={<HelpTooltip text={EMAIL_DESCRIPTION} />}
          input={
            <InputText
              name={emailField}
              control={control}
              editMode={editMode}
              error={emailError}
              helperTextReserveSpace
            />
          }
        />
        <FormElement
          id={`${addressField}.care_of`}
          label={<FormattedMessage defaultMessage="c/o" />}
          visible={editMode || !!careOf}
          input={
            <InputText
              name={`${addressField}.care_of` as never}
              control={control}
              editMode={editMode}
              error={addressErrors?.care_of}
              placeholder={formatMessage({ defaultMessage: 'Namn' })}
              helperTextReserveSpace
            />
          }
        />
        <FormElement
          id={`${addressField}.street`}
          label={<FormattedMessage defaultMessage="Adress" />}
          required={editMode}
          input={
            <InputText
              name={`${addressField}.street` as never}
              control={control}
              editMode={editMode}
              error={addressErrors?.street}
              helperTextReserveSpace
            />
          }
        />
        <FormElement
          id={`${addressField}.zip_code`}
          label={<FormattedMessage defaultMessage="Postnummer" />}
          required={editMode}
          input={
            <InputPostalCode
              name={`${addressField}.zip_code` as never}
              control={control}
              editMode={editMode}
              error={addressErrors?.zip_code}
              helperTextReserveSpace
            />
          }
        />
        <FormElement
          id={`${addressField}.city`}
          label={<FormattedMessage defaultMessage="Postort" />}
          required={editMode}
          input={
            <InputText
              name={`${addressField}.city` as never}
              control={control}
              editMode={editMode}
              error={addressErrors?.city}
              helperTextReserveSpace
            />
          }
        />
        <FormElement
          id={busStopField}
          label={<FormattedMessage defaultMessage="Hållplats" />}
          input={
            <InputBusStop
              name={busStopField}
              control={control}
              editMode={editMode}
              error={busStopError}
              helperTextReserveSpace
              helperText={formatMessage({
                defaultMessage:
                  'Specifikt hållplatsnamn för biljettutställning. Även om adress skulle vara angivet så kommer biljettköp misslyckas om hållplatsen inte finns.',
              })}
            />
          }
        />
      </div>
    </div>
  );
};
